import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ENUMS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faSave } from '@fortawesome/pro-solid-svg-icons';

const OrderAvailableForm = NiceModal.create(({ orderID, status, handleCloseModal }) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				markAsNotAvailableReason: yup.string().nullable().required()
			}),
		[]
	);

	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);

	const axios = useAxiosPrivate();
	const modal = useModal();
	const dispatch = useDispatch();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			markAsNotAvailableReason: null
		}
	});

	const { handleSubmit, setValue, watch } = formMethods;

	const colProps = (rest) => ({
		md: 6,
		...rest
	});

	useEffect(() => {
		const orderNumber = orderID;

		setValue('orderNumber', orderNumber);
	}, [orderID]);

	const onSubmit = (data) => {
		setIsLoading(true);
		axios
			.post(ORDER_ACTIONS.MARK_AS_NOT_AVAILABLE, {
				orderID,
				notAvailableReason: data.markAsNotAvailableReason
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
					modal.remove();
					handleCloseModal && handleCloseModal();
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
			})
			.finally(() => {
				setIsLoading(false);
				modal.remove();
			});
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-notAvailable">
			<ModalTitle>
				<Typography variant="p1">markAsNotAvailable</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="status" colSpan={colProps()}>
						<Status name="status" value={status} />
					</FormItem>

					<FormItem label="orderNumber" colSpan={colProps()}>
						<StringInput name="orderNumber" disabled />
					</FormItem>

					<FormItem label="markAsNotAvailableReason" required>
						<EnumSelect
							name="markAsNotAvailableReason"
							enumName={ENUMS.MARK_AS_NOT_AVAILABLE_REASON_CONFIG}
							setValue={(value) => {
								setValue('markAsNotAvailableReason', value, { shouldValidate: true });
							}}
							searchable={false}
							value={watch('markAsNotAvailableReason')}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					variant="outline"
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
				/>
				<Button
					icon={faSave}
					variant="blue"
					label="save"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default OrderAvailableForm;
